/**
 * Демонстрационные страницы для отработки attachment lifecycle.
 * При форке скелета удалить или модифицировать под конкретное приложение.
 */

import type { ApiViolation } from './client';
import { apiPost, apiPostNoContent } from './client';
import { toDocumentFileReference } from './documents';

export interface ProductImage {
  id: string;
  uri: string;
  filename?: string;
  description?: string;
  size?: number;
  section?: string;
}

export interface ProductDocumentItem {
  id: string;
  createdAt: string;
  file: ProductImage;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  category?: string;
  costPrice?: string;
  salePrice?: string;
  image?: ProductImage | null;
  documents?: ProductDocumentItem[];
  /** Только в ответе POST /api/products/find */
  referencedInOrdersOrShifts?: boolean;
}

export interface FindProductsParams {
  search?: string;
  category?: string;
  page?: number;
  itemsPerPage?: number;
  order?: Array<{ field: string; direction: string }>;
}

export interface FindProductsResponse {
  items: Product[];
  count: number;
}

export interface FindByIDsResponse {
  items: Record<string, Product>;
}

/** Файл для documents (UploadedFile-совместимый). */
export interface ProductDocumentFileInput {
  id: string;
  uri: string;
  filename?: string;
  size?: number;
}

export interface CreateProductParams {
  name: string;
  description?: string;
  category?: string;
  costPrice?: string;
  salePrice?: string;
  image?: ProductImage | null;
  documents?: Array<{ file: ProductDocumentFileInput }>;
}

export interface CreateProductResponse {
  product: Product;
}

export interface UpdateProductParams {
  id: string;
  name: string;
  description?: string;
  category?: string;
  costPrice?: string;
  salePrice?: string;
  image?: ProductImage | null;
  documents: Array<{ file: ProductDocumentFileInput }>;
}

export interface UpdateProductResponse {
  product: Product;
}

export async function findProducts(
  params: FindProductsParams = {},
): Promise<FindProductsResponse> {
  return apiPost<FindProductsResponse>('/api/products/find', {
    search: params.search ?? '',
    category: params.category ?? '',
    page: params.page ?? 1,
    itemsPerPage: params.itemsPerPage ?? 20,
    order: params.order ?? [{ field: 'id', direction: 'desc' }],
  });
}

export async function findProductsByIDs(ids: string[]): Promise<FindByIDsResponse> {
  return apiPost<FindByIDsResponse>('/api/products/find-by-ids', { ids });
}

const DOCUMENTS_SECTION = 'documents';

export async function createProduct(params: CreateProductParams): Promise<CreateProductResponse> {
  const documents =
    params.documents?.map((d) => ({
      file: toDocumentFileReference(d.file, DOCUMENTS_SECTION),
    })) ?? [];

  return apiPost<CreateProductResponse>('/api/products/create', {
    name: params.name,
    description: params.description ?? '',
    category: params.category ?? '',
    costPrice: params.costPrice ?? '',
    salePrice: params.salePrice ?? '',
    image: params.image ?? null,
    documents,
  });
}

export async function updateProduct(params: UpdateProductParams): Promise<UpdateProductResponse> {
  const documents = params.documents.map((d) => ({
    file: toDocumentFileReference(d.file, DOCUMENTS_SECTION),
  }));

  return apiPost<UpdateProductResponse>('/api/products/update', {
    id: params.id,
    name: params.name,
    description: params.description ?? '',
    category: params.category ?? '',
    costPrice: params.costPrice ?? '',
    salePrice: params.salePrice ?? '',
    image: params.image ?? null,
    documents,
  });
}

export async function deleteProducts(ids: string[]): Promise<void> {
  await apiPostNoContent('/api/products/delete', { ids });
}

export interface FindCategoriesResponse {
  items: Array<{ value: string }>;
}

export async function getCategories(): Promise<string[]> {
  const res = await apiPost<FindCategoriesResponse>('/api/products/find-categories', {});
  return res.items.map((item) => item.value);
}

/** Violation по полю `documents` (в т.ч. вложенные пути элементов массива). */
export function isProductDocumentsViolation(v: ApiViolation): boolean {
  const p = v.propertyPath.trim();
  if (p === 'documents') {
    return true;
  }
  if (p.startsWith('documents.')) {
    return true;
  }
  if (p.startsWith('documents[')) {
    return true;
  }
  return false;
}

/** Violation по полю `image` (в т.ч. вложенные свойства file-reference). */
export function isProductImageViolation(v: ApiViolation): boolean {
  const p = v.propertyPath.trim();
  if (p === 'image') {
    return true;
  }
  if (p.startsWith('image.')) {
    return true;
  }
  if (p.startsWith('image[')) {
    return true;
  }
  return false;
}

/**
 * Текст для блока «Документы» при 422: без сырого EN с бэка, если известен шаблон.
 */
export function userMessageForProductDocumentsViolation(v: ApiViolation): string {
  const m = v.message.trim();
  if (/[а-яА-ЯёЁ]/.test(m)) {
    return m;
  }
  const lower = m.toLowerCase();
  if (lower.includes('duplicate') && lower.includes('document')) {
    return 'Один и тот же файл указан дважды. Удалите дубликат из списка.';
  }
  if (lower.includes('section') && lower.includes('document')) {
    return 'Файл документа должен быть из секции «documents». Перезагрузите файл через форму.';
  }
  if (lower.includes('filename')) {
    return 'Укажите имя файла для каждого документа.';
  }
  if (lower.includes('file id') || lower.includes('identifier')) {
    return 'Для каждого документа нужен корректный идентификатор файла после загрузки.';
  }
  if (lower.includes('file') && lower.includes('required')) {
    return 'Для каждой позиции в списке документов нужен прикреплённый файл.';
  }
  if (lower.includes('max') && lower.includes('count')) {
    return 'Слишком много документов. Удалите лишние вложения.';
  }
  return 'Проверьте список документов.';
}

/**
 * Текст для поля «Изображение» при 422.
 */
export function userMessageForProductImageViolation(v: ApiViolation): string {
  const m = v.message.trim();
  if (/[а-яА-ЯёЁ]/.test(m)) {
    return m;
  }
  const lower = m.toLowerCase();
  if (lower.includes('section')) {
    return 'Изображение должно быть из секции «images». Выберите файл снова.';
  }
  if (lower.includes('not found') || lower.includes('unknown')) {
    return 'Файл изображения не найден в хранилище. Загрузите изображение заново.';
  }
  return 'Проверьте изображение товара.';
}

/** Преобразует UploadedFile в ProductImage для API. */
export function toProductImage(
  file: { id: string; uri: string; filename?: string; size?: number },
  section: string,
): ProductImage {
  return {
    id: file.id,
    uri: file.uri,
    filename: file.filename ?? '',
    description: '',
    size: file.size ?? 0,
    section,
  };
}
